import Noty from 'noty';
import lang from '../../lang';
import Env from '../enums/Env';
import { isNumeric } from './utils';
import { Ziggy } from '../../ziggy';
import Locale from '../enums/Locale';
import intlTelInput from 'intl-tel-input';
import isString from 'lodash-es/isString';
import { default as slugit } from 'slugify';
import { HTMLSelector, Toast } from './types';
import { plainToClass } from 'class-transformer';
import Practitioner from '../models/Practitioner';
import Router from '../../../../../vendor/tightenco/ziggy/src/js/Router';

declare global {
    interface Window {
        doctori: any;
        Noty: any;
    }
}

/**
 * Directory separator
 *
 * @type {String}
 */
export const DS = '/';

/**
 * iniTimezone, is application timezone that is defined either by
 * date_default_timezone_set() (if any) or date.timezone ini option (if set)
 *
 * @type {String}
 */
export const TIMEZONE = window.doctori.iniTimezone;

/**
 * Get or check the current application environment.
 *
 * @param  {string|array}  environments
 * @return {string|boolean}
 */
export function environment(...environments) {
    if (environments.length === 0) {
        return window.doctori.env;
    }

    let envs = Array.isArray(environments[0]) ? environments[0] : environments;

    return envs.includes(window.doctori.env);
}

/**
 * Determine if the application is in the production environment.
 *
 * @return {boolean}
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function isProduction() {
    return window.doctori.env === Env.PRODUCTION;
}

/**
 * Determine if the application is in the local environment.
 *
 * @return {boolean}
 */
export function isLocal() {
    return window.doctori.env === Env.LOCAL;
}

/**
 * Check if locale is french
 *
 * @return {Boolean}
 */
export function isFr() {
    return appLocale(Locale.FR);
}

/**
 * Check if given locale is current locale
 *
 * @param {String} [locale]
 * @returns {Boolean}
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function currentLocaleIs(locale: string) {
    return window.doctori.locale === locale;
}

/**
 * Get current locale
 *
 * @returns {String}
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function currentLocale() {
    return window.doctori.locale;
}

/**
 * Get application locale | Check given locale
 *
 * @return {String}
 *
 * @deprecated use currentLocale()/currentLocaleIs() for readability reason
 */
export function appLocale(locale?: string) {
    const _appLocale = window.doctori.locale;
    return locale ? locale === _appLocale : _appLocale;
}

/**
 * Get localized base url
 *
 * @param {String} path
 * @param {String} locale
 * @return {String}
 */
export function localizedBaseUrl(path = '', locale = '') {
    return baseUrl(path, true, locale);
}

/**
 * Get base url
 *
 * @param {String} path
 * @param {Boolean} localized
 * @param {String} lang
 * @return {String}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function baseUrl(path = '', localized = false, lang = '') {
    const host = window.doctori.schemeAndHttpHost;

    const _locale = lang ? lang : appLocale();

    const base_url = localized ? `${host}${DS}${_locale}` : host;

    const pathname = path && !path.startsWith('?') ? `${DS}${path}` : path;

    return path ? `${base_url}${pathname}` : base_url;
}

/**
 * Check if the current user is authenticated
 *
 * @return {Boolean}
 */
export function isAuthenticated() {
    return !!window.doctori.authenticated;
}

/**
 * Check if the current user is not authenticated
 *
 * @return {Boolean}
 */
export function isNotAuthenticated() {
    return !isAuthenticated();
}

/**
 * Get current route name
 *
 * @return {String|null}
 */
export function currentRouteName() {
    var currentRouteName = window.doctori.currentRouteName;

    return currentRouteName ? currentRouteName.toLowerCase() : null;
}

/**
 * check if current route is home
 *
 * @return {Boolean}
 */
export function isHome() {
    return currentRouteName() == 'home';
}

/**
 * Ziggy route
 * @see vendor/tightenco/ziggy/src/js/Router.js
 *
 * @param  {String} name - Route name.
 * @param  {(String|Number|Array|Object)} params - Route parameters.
 * @param  {Boolean} absolute - Whether to include the URL origin.
 * @param  {Boolean} localized
 * @return {String}
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function route(name, params = {}, absolute = true, localized = false) {
    if (localized && Ziggy.url.indexOf(appLocale()) === -1) {
        Ziggy.url = Ziggy.url + DS + appLocale();
    }

    let router = new Router(name, params, absolute, Ziggy);

    return router.toString();
}

/**
 * Localized ziggy route
 *
 * @param  {String} name - Route name.
 * @param  {(String|Number|Array|Object)} params - Route parameters.
 * @param  {Boolean} absolute - Whether to include the URL origin.
 * @return {Function}
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function localizedRoute(name, params = {}, absolute = true) {
    return route(name, params, absolute, true);
}

/**
 * Get the current application guard.
 *
 * @return {string}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function guard() {
    return window.doctori.guard;
}

/**
 * Generate an asset path.
 *
 * @param {String} path
 * @return {String}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function asset(path) {
    return baseUrl(path);
}

/**
 * Parse url
 *
 * @param  {String} url
 * @return {Object}
 */
export function parseUrl(url) {
    var parser = document.createElement('a');

    parser.href = url;

    // parser.protocol; // => "http:"
    // parser.hostname; // => "example.com"
    // parser.port;     // => "3000"
    // parser.pathname; // => "/pathname/"
    // parser.search;   // => "?search=test"
    // parser.hash;     // => "#hash"
    // parser.host;     // => "example.com:3000"

    return parser;
}

/**
 * Copy text to clipboard with navigator API
 *
 * @see https://stackoverflow.com/a/30810322/4325011
 *
 * @param  {String} textToCopy
 * @return {Promise}
 */
export async function copyTextToClipboard(textToCopy) {
    var text = $.trim(textToCopy);

    if (!navigator.clipboard) {
        return await fallbackCopyTextToClipboard(text);
    }

    return navigator.clipboard.writeText(text).then(
        function () {
            return true;
        },
        function () {
            return false;
        }
    );
}

/**
 * Copy text to clipboard (fallback)
 *
 * @param  {String} text
 * @return {Boolean}
 */
export function fallbackCopyTextToClipboard(text) {
    var copied = false;
    let textArea = document.createElement('textarea');

    //
    // *** This styling is an extra step which is likely not required. ***
    //
    // Why is it here? To ensure:
    // 1. the element is able to have focus and selection.
    // 2. if element was to flash render it has minimal visual impact.
    // 3. less flakyness with selection and copying which **might** occur if
    //    the textarea element is not visible.
    //
    // The likelihood is the element won't even render, not even a
    // flash, so some of these are just precautions. However in
    // Internet Explorer the element is visible whilst the popup
    // box asking the user for permission for the web page to
    // copy to the clipboard.
    //

    // Place in top-left corner of screen regardless of scroll position.
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.left = '0';

    // Ensure it has a small width and height. Setting to 1px / 1em
    // doesn't work as this gives a negative w/h on some browsers.
    textArea.style.width = '2em';
    textArea.style.height = '2em';

    // We don't need padding, reducing the size if it does flash render.
    textArea.style.padding = '0';

    // Clean up any borders.
    textArea.style.border = 'none';
    textArea.style.outline = 'none';
    textArea.style.boxShadow = 'none';

    // Avoid flash of white box if rendered for any reason.
    textArea.style.background = 'transparent';

    textArea.value = text;
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
        copied = document.execCommand('copy');
    } catch (err) {}

    document.body.removeChild(textArea);

    return copied;
}

/**
 * Redirect to a given url
 *
 * @param  {String} url
 * @return {Void}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function redirect(url) {
    // similar behavior as clicking on a link
    window.location.href = url;
}

/**
 * Reload the page
 *
 * @return {Void}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function reload() {
    return redirect(document.URL);
}

/**
 * Get last segment of a url
 *
 * @param  {mixed} url
 * @return {String}
 */
export function lastSegment(url) {
    return new URL(url || document.URL).pathname.split('/').pop();
}

/**
 * Set param to url
 *
 * @param {string} key
 * @param {string} value
 * @param url
 * @return {string}
 */
export function setParamToUrl(key, value, url = null) {
    url = url || document.URL;

    url = new URL(url);

    url.searchParams.set(key, value);

    return url.href;
}

/**
 * Check if the given string is a valid email
 *
 * @param  {string}  email
 * @return {boolean}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function isEmail(email) {
    var re =
        /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return re.test(email);
}

/**
 * Check if the given string is not a valid valid
 *
 * @param {string} email
 * @return {boolean}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function isNotEmail(email) {
    return !isEmail(email);
}

/**
 * Check if the given phone number is not a valid one
 * PS: Customized for moroccan numbers only
 *
 * @param {string} phone_number
 * @return {boolean}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function isNotPhoneNumber(phone_number = '') {
    return !isPhoneNumber(phone_number);
}

/**
 * Check if the given phone number is a valid one
 * PS: Customized for moroccan numbers only
 *
 * @param {string} phone_number
 * @return {boolean}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function isPhoneNumber(phone_number = '') {
    if (!phone_number) {
        return false;
    }

    let lineNumber = getLineNumber(phone_number);

    return lineNumber.length === 9 && isNumeric(lineNumber);
}

/**
 * Internationalize the phone number
 * PS: Customized for moroccan numbers only
 *
 * @param {string} phone_number
 * @return {string}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function internationalize(phone_number = '') {
    if (!phone_number) return '';

    let dialCode = '+212';
    let lineNumber = getLineNumber(phone_number);

    return `${dialCode}${lineNumber}`;
}

/**
 * Check if the phone number is not a landline number
 *
 * @param {string} phoneNumber
 * @return {boolean}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function isNotLandlineNumber(phoneNumber = '') {
    return !isLandlineNumber(phoneNumber);
}

/**
 * Check if the phone number is landline
 *
 * @param {string} phoneNumber
 * @return {boolean}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function isLandlineNumber(phoneNumber = '') {
    if (!phoneNumber) return false;

    return getLineNumber(phoneNumber).startsWith('5');
}

/**
 * Get line number (subscriber number)
 * PS: Customized for moroccan numbers only
 *
 * @param {string} phone_number
 * @return {string}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function getLineNumber(phone_number) {
    let lineNumber = '';

    if (phone_number.startsWith('+212')) {
        lineNumber = phone_number.slice(4);
    } else if (phone_number.startsWith('212')) {
        lineNumber = phone_number.slice(3);
    } else if (phone_number.startsWith('0')) {
        lineNumber = phone_number.slice(1);
    }

    return lineNumber;
}

/**
 *  Phone number with readable form
 *
 * @param phone_number
 * @param prefix
 * @returns {string}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function prettifyPhone(phone_number, prefix = '') {
    if (!phone_number) return '';

    let lineNumber = getLineNumber(phone_number);
    return `${prefix}${lineNumber}`.match(/.{1,2}/g).join(' ');
}

/**
 * Get practitioner
 *
 * @return {Practitioner|*}
 */
export function practitioner(attribute = null) {

    let practitioner = plainToClass(Practitioner, window.doctori.practitioner);

    return attribute ? practitioner[attribute] : practitioner;
}

/**
 * Get patient
 *
 * @param {string|null} attribute
 * @return {Patient|*}
 *
 * @deprecated use getPatient in helpers (separation of concern)
 */
export function patient(attribute = null) {
    return attribute ? window.app.patient[attribute] : window.app.patient;
}

/**
 * Get window width
 *
 * @see https://stackoverflow.com/a/28241682/4325011
 * @return {number}
 */
export function width() {
    return window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
}

/**
 * Get window height
 *
 * @see https://stackoverflow.com/a/28241682/4325011
 * @return {number}
 */
export function height() {
    return (
        window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight
    );
}

/**
 * Check if browser under tablet width
 *
 * @param  {number}  size
 * @return {boolean}
 */
export function isTabletSize(size = 991) {
    return matchMediaQuery(`(max-width: ${size}px)`);
}

/**
 * Check if browser is in desktop size
 *
 * @param  {number}  size
 * @return {boolean}
 */
export function isDesktopSize(size = 991) {
    return matchMediaQuery(`(min-width: ${size}px)`);
}

/**
 * Check if window media query matches the given media query
 *
 * @param {string} mediaQuery
 * @return {boolean}
 */
export function matchMediaQuery(mediaQuery) {
    return matchMedia(mediaQuery).matches;
}

/**
 * Init intl tel input
 *
 * @param {Element} input
 * @return {*}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function initIntlInput(input) {
    return intlTelInput(input, {
        placeholderNumberType: guessPhoneNumberType(input),
        initialCountry: '',
        preferredCountries: ['ma', 'fr'],
        excludeCountries: ['eh'],
        // geoIpLookup: function(callback) {
        // 	$.get('https://ipinfo.io', function() {}, "jsonp").always(function(resp) {
        // 		var countryCode = (resp && resp.country) ? resp.country : "";
        // 		callback(countryCode);
        // 	});
        // },
        utilsScript: require('@public/js/utils'),
    });
}

/**
 * Get intl instance from input element
 *
 * @param {HTMLElement} input
 * @return {Object}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function intlInstance(input) {
    return window.intlTelInputGlobals.getInstance(input);
}

/**
 * filter() for Objects
 *
 * @see https://stackoverflow.com/a/37616104/4325011
 *
 * @param obj
 * @param predicate
 */
export function objectFilter(obj, predicate) {
    return Object.keys(obj)
        .filter((key) => predicate(obj[key]))
        .reduce((res, key) => ((res[key] = obj[key]), res), {});
}

/**
 * @see https://github.com/shaneiadt/infinite-scroll/blob/b2372290e65960432301764c99f28776ae39c0ef/src/utils.ts
 *
 * @param {string|Element} selector
 * @param {object} attributes
 * @return {Element}
 */
export function setAttributes(selector, attributes) {
    let element = selector instanceof Element ? selector : query(selector);

    for (const key in attributes) {
        element.setAttribute(key, attributes[key]);
    }

    return element;
}

/**
 * Get Element value
 *
 * @param {string|Element} selector
 * @param {*} [_default]
 * @return {*}
 */
export function getElementValue(selector, _default?: any) {
    let element = toElement(selector);
    let value   = element.value ? element.value.trim() : element.value;
    return value ? value : _default ? _default : null;
}

/**
 * Set Element value
 *
 * @todo testing
 *
 * @param {string|Element} selector
 * @param {*} [value]
 * @return {Element}
 */
export function setElementValue(selector, value?: string | number) {
    let element = toElement(selector);

    if (element) {
        // if value is number and it's zero, zero is falsy which means it always default to empty string '' => bug
        // solution => first check if it's numeric, if it's the case set it directory else use the old way
        element.value = isNumeric(value) ? value : (value || '');
    }

    return element;
}

/**
 * Set element innerHTML
 *
 * @param {string|Element} selector
 * @param {string|number} [html]
 * @param {boolean} [append]
 * @returns {Element|null}
 */
export function setElementHtml(selector, html?: string | number, append: boolean = false) {
    let element = toElement(selector);

    html = isNumeric(html) ? html : (html || '');

    element && (append ? (element.innerHTML += html) : (element.innerHTML = html));

    return element;
}

/**
 * Add hidden class to an element
 *
 * @param {HTMLSelector} selector
 * @param {string} [className]
 */
export function hideElement(selector: HTMLSelector, className?: string) {
    toggleElementVisibility(selector, 'hide', className);
}

/**
 * Remove hidden class from an element
 *
 * @param {HTMLSelector} selector
 * @param {string} [className]
 */
export function showElement(selector: HTMLSelector, className?: string) {
    toggleElementVisibility(selector, 'show', className);
}

/**
 * Add hidden class to an element
 *
 * @param {HTMLSelector} selector
 * @param {string} [className]
 * @returns {void}
 */
export function toggleElementVisibility(selector: HTMLSelector, state: 'show' | 'hide', className?: string) {
    className = className || 'hidden';

    if (isElement(selector)) {
        state === 'hide' ? addClass(selector, className) : removeClass(selector, className);
        return;
    }

    queryAll(selector as string).forEach((element: Element) => {
        state === 'hide' ? addClass(element, className) : removeClass(element, className);
    });
}

/**
 * Add class to element
 *
 * @param {Element|string} selector
 * @param  {string} className
 * @return {Element|null}
 */
export function addClass(selector, className) {
    let element = toElement(selector);

    element && element.classList.add(className);

    return element;
}

/**
 * Remove class to element
 *
 * @param {Element|string} selector
 * @param  {string} className
 * @return {Element|null}
 */
export function removeClass(selector, className) {
    let element = toElement(selector);

    element && element.classList.remove(className);

    return element;
}

/**
 * HTML string to Element
 * @see {@link https://stackoverflow.com/a/35385518/4325011|StackOverFlow}
 *
 * @param {string} html representing a single element
 * @return {Element}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function htmlToElement(html) {
    let template = document.createElement('template');

    template.innerHTML = html;

    return template.content.firstElementChild;
}

/**
 * Add shadow pulse to element
 *
 * @param {string|Element} element
 * @param {number} [timeout]
 * @return {Element}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function shadowPulse(element, timeout = 4000) {
    element = element instanceof Element ? element : query(element);

    element.classList.add('pulsy');

    window.setTimeout(() => element.classList.remove('pulsy'), timeout);

    return element;
}

/**
 * Add or remove one or more classes from each element in the set of matched elements,
 * depending on either the class's presence or the value of the state argument.
 *
 * @param {string|Element} selector
 * @param {string} className
 * @param {boolean} [flag]
 * @return {Element}
 */
export function toggleClass(selector, className, flag) {
    let element = selector instanceof Element ? selector : query(selector);
    console.log('element : ', element);
    let classes = new Set(element.className.split(' '));
    console.log('classes : ', classes);
    if (typeof flag === 'boolean') {
        console.log('flag : ', flag);
        flag ? classes.add(className) : classes.delete(className);
        console.log('classes : ', classes);
        console.log("Array.from(classes).join(' ') : ", Array.from(classes).join(' '));
        element.className = Array.from(classes).join(' ');
        console.log('el : ', element);
        return element;
    }

    classes.has(className) ? classes.delete(className) : classes.add(className);

    element.className = Array.from(classes).join(' ');
    console.log("Array.from(classes).join(' ')", Array.from(classes).join(' '));
    return element;
}

/**
 * Get element by id
 *
 * @param {string} elementId
 * @return {HTMLElement}
 */
export function byId(elementId) {
    if (elementId.startsWith('#')) {
        elementId = elementId.substring(1);
    }

    return document.getElementById(elementId);
}

/**
 * Get element by class
 *
 * @param classNames
 * @return {HTMLCollectionOf<Element>}
 */
export function byClass(classNames) {
    return document.getElementsByClassName(classNames);
}

/**
 * Returns the first element that is a descendant of node that matches selectors.
 *
 * @param {string|Element} selector
 * @param {Document|HTMLElement} parent
 * @return {Element|null}
 *
 * @deprecated use the one in utils (separation of concern)
 */
export function query(selector, parent = document) {
    return selector instanceof Element ? selector : parent.querySelector(selector);
}

/**
 * Returns all element descendants of node that match selectors.
 *
 * @param {string} selector
 * @param {Document|Element|HTMLElement} parentElement
 * @return {Array}
 */
export function queryAll(selectors: string, parentElement?: Document | Element | HTMLElement) {
    const parent = parentElement || document;
    return Array.from(parent.querySelectorAll(selectors));
}

/**
 * Success notification
 *
 * @param {string} text
 */
export function successNoty(text) {
    return new window.Noty({
        text: text,
        type: 'success',
        layout: 'bottomRight',
        timeout: 3000,
    }).show();
}

/**
 * Toast notification
 *
 * @param {string} text
 * @param {Noty.Options} config
 * @return {Toast}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function toast(text: string, config: Noty.Options = {}): Toast {

    let defaults = {
        text: text,
        layout: 'bottomRight',
        timeout: 5000,
    };

    let options: Noty.Options = Object.assign({}, defaults, config);

    let noty = new window.Noty(options);

    const optionsOverride = true;

    return {
        instance: noty,

        success: function () {
            return noty.setType('success', optionsOverride).show();
        },

        error: function () {
            return noty.setType('warning', optionsOverride).show();
        },
    };
}

/**
 * Extract validation errors from response
 *
 * @param {Object} response
 * @return {string[]}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
export function validationErrors(response) {
    return Object.values(response.responseJSON.errors).flat();
}

/**
 *
 * @param value
 * @param default_value
 * @return {number}
 */
export function parseToInt(value, default_value = 0) {
    let parsedValue = parseInt(value);

    return isNaN(parsedValue) ? default_value : parsedValue;
}

/**
 * Guess phone number type base on element name
 * @see https://github.com/jackocnr/intl-tel-input/blob/master/src/js/utils.js#L119
 *
 * @param {Element} element
 * @return {string}
 *
 * @deprecated use the one in helpers (separation of concern)
 */
function guessPhoneNumberType(element) {
    let needle = element.name.toLowerCase();
    let haystack = ['mobile'];

    return haystack.includes(needle) ? 'MOBILE' : 'FIXED_LINE';
}

/**
 * Disable an element
 *
 * @param {string|Element} selector
 * @return {void}
 */
export function disableElement(selector) {
    let element = toElement(selector);

    element.disabled = true;
}

/**
 * Enable an element
 *
 * @param {string|Element} selector
 * @return {void}
 */
export function enableElement(selector) {
    let $element = isString(selector) ? document.querySelector(selector) : selector;

    $element.disabled = false;
}

/**
 * Callback for DOMContentLoaded event
 +
 * @callback DOMContentLoadedListener
 * @param {Event} [event]
 * @return {void}
 */
/**
 * The DOMContentLoaded event fires when the initial HTML document has been completely loaded
 * and parsed, without waiting for stylesheets, images, and sub-frames to finish loading.
 *
 * @param {DOMContentLoadedListener} contentLoadedListener
 * @return {void}
 *
 * @deprecated use the one in utils
 */
export function ready(contentLoadedListener) {
    document.addEventListener('DOMContentLoaded', contentLoadedListener);
}

/**
 * Callback for load event
 +
 * @callback DOMAssetsLoadedListener
 * @param {Event} [event]
 * @return {void}
 */
/**
 * The load event is fired when the whole page has loaded, including all dependent resources such as
 * stylesheets and images. This is in contrast to DOMContentLoaded, which is fired as soon as
 * the page DOM has been loaded, without waiting for resources to finish loading.
 *
 * @param {DOMAssetsLoadedListener} assetsLoadedListener
 * @return {void}
 *
 * @deprecated use the one in utils
 */
export function loaded(assetsLoadedListener) {
    window.addEventListener('load', assetsLoadedListener);
}

/**
 * Build URL form query String
 *
 * @param  {String/null} baseUrl
 * @param  {Object/null} queryString
 * @return {String}
 */
export function buildUrl(queryString = null, baseUrl = null) {
    if (!baseUrl) {
        baseUrl = window.location.href.split('?')[0];
        baseUrl = baseUrl.split('#')[0];
    }

    return `${baseUrl}?${queryString ? queryString : ''}`;
}

/**
 *  Jquery param inverse. Inverse of http://api.jquery.com/jquery.param/
 *  @see https://gist.github.com/chriscalip/779f0582597f018d0112e873822f48e3
 *
 * @param params
 * @param coerce
 * @return {{Object}}
 */
export function queryStringToObject(params, coerce) {
    let obj = {};
    let coerce_types = { true: !0, false: !1, null: null };

    // Iterate over all name=value pairs.
    $.each(params.replace(/\+/g, ' ').split('&'), function (j, v) {
        let param = v.split('='),
            key = decodeURIComponent(param[0]),
            val,
            cur = obj,
            i = 0,
            // If key is more complex than 'foo', like 'a[]' or 'a[b][c]', split it
            // into its component parts.
            keys = key.split(']['),
            keys_last = keys.length - 1;

        // If the first keys part contains [ and the last ends with ], then []
        // are correctly balanced.
        if (/\[/.test(keys[0]) && /\]$/.test(keys[keys_last])) {
            // Remove the trailing ] from the last keys part.
            keys[keys_last] = keys[keys_last].replace(/\]$/, '');

            // Split first keys part into two parts on the [ and add them back onto
            // the beginning of the keys array.
            keys = keys.shift().split('[').concat(keys);

            keys_last = keys.length - 1;
        } else {
            // Basic 'foo' style key.
            keys_last = 0;
        }

        // Are we dealing with a name=value pair, or just a name?
        if (param.length === 2) {
            val = decodeURIComponent(param[1]);
            // Coerce values.
            if (coerce) {
                val =
                    val && !isNaN(val)
                        ? +val // number
                        : val === 'undefined'
                        ? undefined // undefined
                        : coerce_types[val] !== undefined
                        ? coerce_types[val] // true, false, null
                        : val; // string
            }
            if (keys_last) {
                // Complex key, build deep object structure based on a few rules:
                // * The 'cur' pointer starts at the object top-level.
                // * [] = array push (n is set to array length), [n] = array if n is
                //   numeric, otherwise object.
                // * If at the last keys part, set the value.
                // * For each keys part, if the current level is undefined create an
                //   object or array based on the type of the next keys part.
                // * Move the 'cur' pointer to the next level.
                // * Rinse & repeat.
                for (; i <= keys_last; i++) {
                    // @ts-ignore
                    key = keys[i] === '' ? cur.length : keys[i];
                    cur = cur[key] =
                        i < keys_last
                            // @ts-ignore
                            ? cur[key] || (keys[i + 1] && isNaN(keys[i + 1]) ? {} : [])
                            : val;
                }
            } else {
                // Simple key, even simpler rules, since only scalars and shallow
                // arrays are allowed.
                if ($.isArray(obj[key])) {
                    // val is already an array, so push on the next value.
                    obj[key].push(val);
                } else if (obj[key] !== undefined) {
                    // val isn't an array, but since a second value has been specified,
                    // convert val into an array.
                    obj[key] = [obj[key], val];
                } else {
                    // val is a scalar.
                    obj[key] = val;
                }
            }
        } else if (key) {
            // No value was defined, so set something meaningful.
            obj[key] = coerce ? undefined : '';
        }
    });

    return obj;
}

/**
 * Rename key in object
 * @see https://stackoverflow.com/a/14592469/4325011
 *
 * @param {object} object
 * @param {string} oldKey
 * @param {string} newKey
 * @return {object}
 */
export function renameKeyObject(object, oldKey, newKey) {
    if (oldKey !== newKey) {
        Object.defineProperty(object, newKey, Object.getOwnPropertyDescriptor(object, oldKey));
        delete object[oldKey];
    }

    return object;
}

/**
 * Translate the given string.
 *
 * @param {string} key
 * @returns {string|null}
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function __(key) {
    return lang.strings(key);
}

/**
 * translation for validation rules
 *
 * @param {string} rule
 * @param {string|object} attribute
 * @return {string}
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function __v(rule, attribute = null ) {
    return lang.validation(rule, attribute);
}

/**
 * Dispatch event
 *
 * @param {string} topic Event topic
 * @param {object|null} data
 * @returns {boolean}
 *
 * @deprecated use the one in support/utils
 */
export function dispatchEvent(topic, data = null) {
    return PubSub.publish(topic, data);
}

/**
 * Handle event
 *
 * @param {string} topic Event topic
 * @param {function} callback
 * @returns {string}
 *
 * @deprecated use the one in support/utils
 */
export function handleEvent(topic, callback) {
    return PubSub.subscribe(topic, callback);
}

/**
 * Dispatch global resize event
 * NB: not used yet
 *
 * @return {void}
 */
export function dispatchResizeEvent() {
    window.dispatchEvent(new Event('resize'));
}

/**
 * Slugify
 * @param {string} text
 * @param {{replacement: string, remove: RegExp, lower: boolean, strict: boolean, locale: string, trim: boolean}} [options]
 *
 * @deprecated use the one in helpers instead (separation of concerns)
 */
export function slugify(text, options = {}) {

    let defaults = {
        replacement: '-', // replace spaces with replacement character, defaults to `-`
        remove: undefined, // remove characters that match regex, defaults to `undefined`
        lower: true, // convert to lower case, defaults to `false` (I changed it to true)
        strict: false, // strip special characters except replacement, defaults to `false`
        locale: 'vi', // language code of the locale to use
        trim: true, // trim leading and trailing replacement chars, defaults to `true`
    };

    let _options = Object.assign({}, defaults, options);

    return slugit(text, _options);
}

/**
 * Selector to Element
 *
 * @param {string|Element} selector
 * @return {Element|null}
 */
function toElement(selector) {
    return isElement(selector) ? selector : query(selector);
}

/**
 * check if a subject is an Element
 *
 * @param {*} subject
 * @return {boolean}
 */
function isElement(subject: any) {
    return subject instanceof Element;
}
